import React, {useCallback, useEffect, useState} from 'react'
import * as PropTypes from 'prop-types'
import {InferPropTypes} from '@modifi/typescript-utils'
import {
  Col,
  Highlight,
  Row,
  ButtonSecondary,
  Caption,
  Body,
  AttentionIcon,
  Tooltip,
  UnstyledButton,
  ButtonLink,
  NextIcon,
  withResponsiveHelper,
  Small,
  SkeletonButton,
  SkeletonLine,
} from '@modifi/ui'
import {warning, neutralSemiDark} from '@modifi/colors'
import {FormattedDate, useTranslation} from '@modifi/plugin-i18n'

import {ShipmentStatus} from '../../../../components'
import {Progress, DocumentList} from './components'
import {Wrapper, ContainerList, ContainerEntry} from './styles'
import {useRenderPersistedArray} from '../../../../hooks'

const ColoredAttentionIcon = React.forwardRef((props, ref) => (
  // @ts-expect-error ignore typing for now
  <UnstyledButton ref={ref} mr={16}>
    <AttentionIcon color={warning} {...props} />
  </UnstyledButton>
))

const propTypes = {
  externalId: PropTypes.string,
  shippingStartDate: PropTypes.string,
  estimatedShippingStartDate: PropTypes.string,
  shippingEndDate: PropTypes.string,
  estimatedShippingEndDate: PropTypes.string,
  shipmentStatus: PropTypes.string,
  lastEventDate: PropTypes.string,
  containers: PropTypes.arrayOf(
    PropTypes.shape({
      containerId: PropTypes.string,
    }).isRequired
  ),
  destination: PropTypes.shape({
    name: PropTypes.string,
    countryCode: PropTypes.string,
  }),
  origin: PropTypes.shape({
    name: PropTypes.string,
    countryCode: PropTypes.string,
  }),
  documentRef: PropTypes.shape({
    documentType: PropTypes.string,
    fileId: PropTypes.string,
  }),
  statusInfo: PropTypes.string,
  showDetails: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  listIndex: PropTypes.number,
  isLgOrGreater: PropTypes.bool,
}
const defaultProps = {
  externalId: undefined as null | undefined,
  shippingStartDate: undefined as null | undefined,
  estimatedShippingStartDate: undefined as null | undefined,
  shippingEndDate: undefined as null | undefined,
  estimatedShippingEndDate: undefined as null | undefined,
  shipmentStatus: undefined as null | undefined,
  lastEventDate: undefined as null | undefined,
  containers: undefined as null | undefined,
  origin: undefined as null | undefined,
  destination: undefined as null | undefined,
  documentRef: undefined as null | undefined,
  statusInfo: undefined as null | undefined,
  isLoading: false,
  listIndex: 0,
  isLgOrGreater: false,
}

type ShipmentEntryProps = InferPropTypes<typeof propTypes, typeof defaultProps>

const ShipmentEntry: React.FC<ShipmentEntryProps> = ({
  externalId,
  shippingStartDate,
  shippingEndDate,
  shipmentStatus,
  lastEventDate,
  origin,
  destination,
  estimatedShippingStartDate,
  estimatedShippingEndDate,
  containers,
  documentRef,
  statusInfo,
  showDetails,
  isLoading,
  listIndex,
  isLgOrGreater,
}) => {
  const {t, ready: tReady} = useTranslation('argus', {useSuspense: false})
  const [startingPort, setStartingPort] = useState('')
  const [destinationPort, setDestinationPort] = useState('')
  const [lastEventUpdateDate, setLastEventUpdateDate] = useState('')

  useEffect(() => {
    if (origin?.name && origin?.countryCode) {
      setStartingPort(origin?.name.concat(',', origin?.countryCode))
    } else {
      setStartingPort(t('empty-data.message'))
    }
  }, [origin, t])

  useEffect(() => {
    if (destination?.name && destination?.countryCode) {
      setDestinationPort(destination?.name.concat(',', destination?.countryCode))
    } else {
      setDestinationPort(t('empty-data.message'))
    }
  }, [destination, t])

  useEffect(() => {
    if (lastEventDate) {
      setLastEventUpdateDate(lastEventDate)
    } else {
      setLastEventUpdateDate(t('empty-data.message'))
    }
  }, [lastEventDate, t])

  const handleDetailsButtonClick = useCallback(() => {
    showDetails(externalId)
  }, [showDetails, externalId])

  const fileIds = useRenderPersistedArray(documentRef?.fileId ? [documentRef.fileId] : [])

  return (
    <Wrapper data-ident="shipments-tracking-list-entry" data-shipment-external-id={externalId}>
      {!isLoading && (
        <Progress
          startDate={shippingStartDate ?? estimatedShippingStartDate}
          endDate={shippingEndDate ?? estimatedShippingEndDate}
          animationIndex={listIndex}
          status={statusInfo ? 'WARNING' : shipmentStatus}
        />
      )}
      <Row>
        <Col col={4} md={3} mdOrder={1}>
          <Row>
            <Col>
              <Highlight>{t('shipment-location-start')}</Highlight>
              <br />
              <Body>
                {isLoading ? (
                  <SkeletonLine data-ident="shipment-status-loading" stringLength={18} />
                ) : (
                  `${startingPort}`
                )}
              </Body>
            </Col>
          </Row>
          <Row>
            <Col>
              <Caption>
                {isLoading ? (
                  <SkeletonLine stringLength={8} />
                ) : (
                  <FormattedDate>{shippingStartDate ?? estimatedShippingStartDate}</FormattedDate>
                )}
              </Caption>
            </Col>
          </Row>
        </Col>

        <Col col={4} md={3} mdOrder={2}>
          <Row>
            <Col>
              <Highlight>{t('shipment-location-destination')}</Highlight>
              <br />
              <Body>
                {isLoading ? <SkeletonLine stringLength={18} /> : <span>{destinationPort}</span>}
              </Body>
            </Col>
          </Row>
          <Row>
            <Col>
              <Caption>
                {isLoading ? (
                  <SkeletonLine stringLength={8} />
                ) : (
                  <FormattedDate>{shippingEndDate ?? estimatedShippingEndDate}</FormattedDate>
                )}
              </Caption>
            </Col>
          </Row>
        </Col>
        <Col col={4} md="auto" mdMl="auto" mdOrder={4}>
          <Row alignItems="center" justifyContent="end">
            {statusInfo && (
              <Col col="auto">
                <Tooltip theme="light" refComponent={ColoredAttentionIcon}>
                  {statusInfo}
                </Tooltip>
              </Col>
            )}
            <Col col="auto">
              <ShipmentStatus
                small={!isLgOrGreater}
                status={isLoading ? 'PLACEHOLDER' : shipmentStatus}
              />
            </Col>
          </Row>
        </Col>
        <Col col="auto" mt={16} mdMt={0} mdOrder={3}>
          <Highlight>
            {!tReady ? <SkeletonLine stringLength={10} /> : t('shipment-list.containers.headline')}
          </Highlight>
          <br />
          <ContainerList>
            {isLoading || !tReady ? (
              <>
                <ContainerEntry>
                  <Body>
                    <SkeletonLine stringLength={10} />
                  </Body>
                </ContainerEntry>
              </>
            ) : (
              containers?.map(({containerId}) => (
                <ContainerEntry key={containerId}>
                  <Body>{containerId}</Body>
                </ContainerEntry>
              ))
            )}
          </ContainerList>
        </Col>

        <Col sm={4} md={3} mt={16} mdOrder={5}>
          {documentRef ? <DocumentList fileIds={fileIds} /> : ' '}
        </Col>
        <Col col={4} mt="auto" mdOrder={6} pb={4} hiddenMdDown>
          <Small color={neutralSemiDark}>
            {' '}
            {t('shipment-detail-containers-last-event-date')} : {lastEventUpdateDate}
          </Small>
        </Col>
        <Col col="auto" ml="auto" mdOrder={7} mt="auto">
          {!tReady || isLoading ? (
            <SkeletonButton>
              <ButtonSecondary data-target="open-shipment-details-dialog-skeleton" inverse>
                Details
              </ButtonSecondary>
            </SkeletonButton>
          ) : (
            <ButtonLink
              data-shipping-start-date={shippingStartDate}
              data-container-ids={containers?.map(i => i.containerId)}
              data-target="open-shipment-details-dialog"
              onClick={handleDetailsButtonClick}
              icon
            >
              <span>{t('cta.details')}</span>
              <NextIcon height={16} width={16} />
            </ButtonLink>
          )}
        </Col>
      </Row>
    </Wrapper>
  )
}

ShipmentEntry.propTypes = propTypes
ShipmentEntry.defaultProps = defaultProps

// @todo remove when withResponsiveHelper is type fixed
export default withResponsiveHelper(ShipmentEntry) as typeof ShipmentEntry
